import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, Check, Pencil, Plus } from "lucide-react";
import { admin, type Reference } from "../lib/api";
import { useStudio } from "../lib/context";
import { ErrorBox, errorMessage, Field, Loading, PageHead } from "../components/ui";

const fmtDay = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const plusYear = (d: string) => `${Number(d.slice(0, 4)) + 1}${d.slice(4)}`;

/**
 * Référentiel des programmes (administrateur) : années scolaires du pays de travail et matières communes à tous les pays.
 * Les programmes eux-mêmes se créent dans la page « Programme » (rôle Éditeur).
 */
export function ReferenceTab() {
  const { countryId } = useStudio();
  const qc = useQueryClient();
  const ref = useQuery({ queryKey: ["admin-reference", countryId], queryFn: () => admin.reference(countryId) });
  const refresh = () => qc.invalidateQueries({ predicate: (q) => ["admin-reference", "curriculum-options", "curricula", "admin-audit"].includes(String(q.queryKey[0])) });

  if (ref.isLoading) return <Loading />;
  if (ref.error) return <ErrorBox error={ref.error} onRetry={() => ref.refetch()} />;
  const data = ref.data!;
  return (
    <div className="stack" style={{ gap: 14 }}>
      <PageHead kicker="Administration" title="Référentiel"
        lead={<>Les <b>années scolaires</b> du pays et les <b>matières</b>, communes à tous les pays. Les programmes se créent ensuite dans la page Programme, par un éditeur.</>} />
      <SchoolYears data={data} onDone={refresh} />
      <Subjects data={data} onDone={refresh} />
    </div>
  );
}

function SchoolYears({ data, onDone }: { data: Reference; onDone: () => void }) {
  const { countryId, toast } = useStudio();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  // Proposition : l'année qui suit la plus récente, aux mêmes dates un an plus tard.
  const start = () => {
    const latest = data.schoolYears[0];
    const m = /^(\d{4})-(\d{4})$/.exec(latest?.label ?? "");
    setLabel(m ? `${Number(m[1]) + 1}-${Number(m[2]) + 1}` : "");
    setStartsOn(latest ? plusYear(latest.startsOn) : "");
    setEndsOn(latest ? plusYear(latest.endsOn) : "");
    setOpen(true);
  };
  const create = useMutation({
    mutationFn: () => admin.createSchoolYear({ countryId, label: label.trim(), startsOn, endsOn }),
    onSuccess: () => { toast(`Année ${label} créée`); setOpen(false); onDone(); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); if (!create.isPending && label && startsOn && endsOn) create.mutate(); };

  return (
    <div className="card stack appear" style={{ gap: 12 }}>
      <div className="row">
        <div className="stack" style={{ gap: 2, flex: 1 }}>
          <h2 style={{ fontSize: 17 }}>Années scolaires</h2>
          <span className="small muted">L'app sert le programme de l'année en cours ; pendant les vacances, celui de l'année qui vient de finir.</span>
        </div>
        {!open && <button className="btn sm" onClick={start}><CalendarPlus size={15} />Nouvelle année</button>}
      </div>
      {open && (
        <form className="row wrap appear" style={{ gap: 10, alignItems: "flex-end" }} onSubmit={submit}>
          <Field label="Libellé"><input className="input" style={{ width: 130 }} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="2027-2028" maxLength={9} autoFocus /></Field>
          <Field label="Rentrée"><input className="input" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} /></Field>
          <Field label="Fin des cours"><input className="input" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} /></Field>
          <button className="btn sm" disabled={create.isPending || !label || !startsOn || !endsOn}>Créer</button>
          <button type="button" className="btn sm ghost" onClick={() => setOpen(false)}>Annuler</button>
        </form>
      )}
      <div className="table-wrap">
        <table className="t">
          <thead><tr><th>Année</th><th>Rentrée</th><th>Fin des cours</th><th>Programmes</th></tr></thead>
          <tbody>
            {data.schoolYears.map((y) => (
              <tr key={y.id}>
                <td><b>{y.label}</b>{y.current && <span className="badge b-VALIDATED" style={{ marginLeft: 8 }}>En cours</span>}</td>
                <td className="small">{fmtDay(y.startsOn)}</td>
                <td className="small">{fmtDay(y.endsOn)}</td>
                <td className="small">{y.curricula || <span className="muted">aucun</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Subjects({ data, onDone }: { data: Reference; onDone: () => void }) {
  const { toast } = useStudio();
  const [open, setOpen] = useState(false);
  const [id, setId] = useState("");
  const [label, setLabel] = useState("");
  const [editing, setEditing] = useState<{ id: string; label: string } | null>(null);
  const create = useMutation({
    mutationFn: () => admin.createSubject({ id, label: label.trim() }),
    onSuccess: () => { toast(`Matière ${label} ajoutée : l'app l'affiche « bientôt disponible » jusqu'à son premier programme publié`); setOpen(false); setId(""); setLabel(""); onDone(); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const rename = useMutation({
    mutationFn: (s: { id: string; label: string }) => admin.renameSubject(s.id, s.label.trim()),
    onSuccess: () => { toast("Matière renommée"); setEditing(null); onDone(); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const validId = /^[A-Z][A-Z0-9_]{1,15}$/.test(id);
  const submit = (e: FormEvent) => { e.preventDefault(); if (!create.isPending && validId && label.trim().length >= 2) create.mutate(); };

  return (
    <div className="card stack appear" style={{ gap: 12 }}>
      <div className="row">
        <div className="stack" style={{ gap: 2, flex: 1 }}>
          <h2 style={{ fontSize: 17 }}>Matières</h2>
          <span className="small muted">Communes à tous les pays. L'identifiant ne change plus une fois créé ; le libellé, si.</span>
        </div>
        {!open && <button className="btn sm" onClick={() => setOpen(true)}><Plus size={15} />Nouvelle matière</button>}
      </div>
      {open && (
        <form className="row wrap appear" style={{ gap: 10, alignItems: "flex-end" }} onSubmit={submit}>
          <Field label="Identifiant" hint="Majuscules, ex. HG"><input className="input mono" style={{ width: 120 }} value={id} onChange={(e) => setId(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))} maxLength={16} autoFocus /></Field>
          <Field label="Libellé"><input className="input" style={{ width: 280 }} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Histoire-Géographie" maxLength={60} /></Field>
          <button className="btn sm" disabled={create.isPending || !validId || label.trim().length < 2}>Ajouter</button>
          <button type="button" className="btn sm ghost" onClick={() => setOpen(false)}>Annuler</button>
        </form>
      )}
      <div className="table-wrap">
        <table className="t">
          <thead><tr><th>Identifiant</th><th>Libellé</th><th>Programmes</th><th>Notions</th><th /></tr></thead>
          <tbody>
            {data.subjects.map((s) => (
              <tr key={s.id}>
                <td className="mono small">{s.id}</td>
                <td>
                  {editing?.id === s.id ? (
                    <form className="row" style={{ gap: 6 }} onSubmit={(e) => { e.preventDefault(); if (editing.label.trim().length >= 2 && !rename.isPending) rename.mutate(editing); }}>
                      <input className="input" style={{ width: 260 }} value={editing.label} maxLength={60} autoFocus aria-label={`Libellé de ${s.id}`}
                        onChange={(e) => setEditing({ id: s.id, label: e.target.value })} onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); }} />
                      <button className="icon-btn bordered" aria-label="Enregistrer" disabled={rename.isPending || editing.label.trim().length < 2}><Check size={15} /></button>
                    </form>
                  ) : <b>{s.label}</b>}
                </td>
                <td className="small">{s.curricula || <span className="muted">aucun</span>}</td>
                <td className="small">{s.notions || <span className="muted">aucune</span>}</td>
                <td>{editing?.id !== s.id && <button className="icon-btn" aria-label={`Renommer ${s.label}`} onClick={() => setEditing({ id: s.id, label: s.label })}><Pencil size={15} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
